import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi, Spinner, ErrBanner, today, fmtDate, fmtNum } from '../../hooks/useApi';
import { api } from '../../services/api';
import ConfirmModal from '../../components/ConfirmModal';
import './Payments.css';

const EMPTY = {
  payment_date: today(),
  supplier_id: '',
  party_name: '',
  payment_type: '',
  bill_no: '',
  bill_amount: '',
  paid_amount: '',
  mode: 'Cash',
  reference_no: '',
  remarks: '',
};

// Payment heads. Salary posts to Expense (shows in P&L / accounting reports);
// Employee Advance posts to the Advances asset (recoverable loan, not an expense).
const PAYMENT_TYPES = [
  { value: '',        labelKey: 'payments.types.expense' },
  { value: 'salary',  labelKey: 'payments.types.salary' },
  { value: 'advance', labelKey: 'payments.types.advance' },
];
const payTypeLabelKey = (v) => (PAYMENT_TYPES.find(t => t.value === (v || ''))?.labelKey);

export default function Payments() {
  const { t: tr } = useTranslation();
  const [date, setDate] = useState(today());
  const { data: payments, loading, error, reload } = useApi(
    () => api.payments.list(date), [date]
  );
  const { data: suppliers } = useApi(() => api.suppliers.list(), [], []);
  const { data: banksData } = useApi(() => api.banks.list(), [], { banks: [] });
  const { data: dReceipts } = useApi(() => api.dailyReceipts.list(''), []);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [msg, setMsg] = useState('');
  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };

  const onSupplierChange = (e) => {
    const sid = e.target.value;
    const sup = (suppliers || []).find(s => String(s.id) === sid);
    setForm(f => ({
      ...f,
      supplier_id: sid,
      party_name: sup ? sup.name : f.party_name,
    }));
  };

  const handleSave = async () => {
    if (!form.party_name.trim()) { notify('❌ ' + tr('payments.errors.partyRequired')); return; }
    const paid = parseFloat(form.paid_amount || 0);
    if (paid <= 0) { notify('❌ ' + tr('payments.errors.paidGtZero')); return; }
    setSaving(true);
    try {
      await api.payments.create({
        ...form,
        supplier_id: form.supplier_id ? parseInt(form.supplier_id) : null,
        paid_amount: paid,
        bill_amount: parseFloat(form.bill_amount || 0),
      });
      notify('✅ ' + tr('payments.toasts.recorded'));
      setShowForm(false);
      setForm(EMPTY);
      reload();
    } catch (e) {
      notify('❌ ' + e.message);
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    try {
      await api.payments.remove(confirm.id);
      notify('✅ ' + tr('payments.toasts.deleted'));
      reload();
    } catch (e) {
      notify('❌ ' + e.message);
    }
    setConfirm(null);
  };

  const total = (payments || []).reduce((s, p) => s + Number(p.paid_amount || 0), 0);
  const banks = banksData.banks || [];

  return (
    <div className="payments-page">
      <div className="page-header">
        <h2 className="page-title">{tr('payments.title')}</h2>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => { setShowForm(!showForm); setForm(EMPTY); }}
        >
          {showForm ? `✕ ${tr('common.cancel')}` : `＋ ${tr('payments.newPayment')}`}
        </button>
      </div>

      {msg && <div className="pay-toast">{msg}</div>}
      {error && <ErrBanner msg={error} />}

      {showForm && (
        <div className="section-card">
          <div className="section-header">{tr('payments.recordNew')}</div>
          <div className="pay-form-grid">
            <div className="form-group">
              <label>{tr('common.date')} *</label>
              <input type="date" value={form.payment_date}
                     onChange={e => setForm({ ...form, payment_date: e.target.value })} />
            </div>
            <div className="form-group">
              <label>{tr('payments.supplierOptional')}</label>
              <select value={form.supplier_id} onChange={onSupplierChange}>
                <option value="">{tr('payments.otherParty')}</option>
                {(suppliers || []).map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>{tr('payments.paymentType')}</label>
              <select value={form.payment_type}
                      onChange={e => setForm({ ...form, payment_type: e.target.value })}>
                {PAYMENT_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{tr(t.labelKey)}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>{tr('payments.partyName')} *</label>
              <input value={form.party_name}
                     onChange={e => setForm({ ...form, party_name: e.target.value })} />
            </div>
            <div className="form-group">
              <label>{tr('payments.billNoFromReceipts')}</label>
              <select value={form.bill_no} onChange={e => {
                const val = e.target.value;
                const match = (dReceipts || []).find(r => r.receipt_no === val);
                setForm({ ...form, bill_no: val, bill_amount: match ? match.amount : form.bill_amount });
              }}>
                <option value="">{tr('payments.selectBill')}</option>
                {Array.from(new Set((dReceipts || []).map(r => r.receipt_no))).filter(Boolean).map(b => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>{tr('payments.billAmount')}</label>
              <input type="number" step="0.01" value={form.bill_amount}
                     onChange={e => setForm({ ...form, bill_amount: e.target.value })} />
            </div>
            <div className="form-group">
              <label>{tr('payments.paidAmount')} *</label>
              <input type="number" step="0.01" value={form.paid_amount}
                     onChange={e => setForm({ ...form, paid_amount: e.target.value })} />
            </div>
            <div className="form-group">
              <label>{tr('payments.mode')} *</label>
              <select value={form.mode} onChange={e => setForm({ ...form, mode: e.target.value })}>
                <option value="Cash">{tr('common.cash')}</option>
                {banks.map(b => (
                  <option key={b.id} value={`Bank:${b.id}`}>
                    {b.name}{Number(b.is_default) === 1 ? ` (${tr('payments.default')})` : ''}
                  </option>
                ))}
                <option value="UPI">{`UPI (${tr('payments.defaultBank')})`}</option>
                <option value="NEFT">{`NEFT (${tr('payments.defaultBank')})`}</option>
                <option value="RTGS">{`RTGS (${tr('payments.defaultBank')})`}</option>
                <option value="Cheque">{`${tr('payments.cheque')} (${tr('payments.defaultBank')})`}</option>
              </select>
            </div>
            <div className="form-group">
              <label>{tr('payments.referenceNo')}</label>
              <input value={form.reference_no}
                     onChange={e => setForm({ ...form, reference_no: e.target.value })} />
            </div>
            <div className="form-group form-group-wide">
              <label>{tr('common.remarks')}</label>
              <input value={form.remarks}
                     onChange={e => setForm({ ...form, remarks: e.target.value })} />
            </div>
          </div>
          <div style={{ padding: '0 12px 12px' }}>
            <button className="btn btn-primary btn-sm" disabled={saving} onClick={handleSave}>
              {saving ? `⏳ ${tr('common.saving')}` : tr('payments.savePayment')}
            </button>
          </div>
        </div>
      )}

      <div className="pay-filters">
        <div className="form-group">
          <label>{tr('payments.filterByDate')}</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} />
        </div>
        <div style={{ marginLeft: 'auto', alignSelf: 'center' }}>
          <strong>{tr('payments.totalPaidOn', { date: fmtDate(date) })} </strong>
          <span style={{ fontWeight: 700, fontFamily: 'monospace', color: 'var(--danger)' }}>
            ₹ {fmtNum(total)}
          </span>
        </div>
      </div>

      {loading ? <Spinner /> : (
        <div className="section-card" style={{ overflowX: 'auto' }}>
          <div className="section-header">{tr('payments.paymentsOn', { date: fmtDate(date) })}</div>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>{tr('payments.paymentNo')}</th>
                <th style={{ textAlign: 'left' }}>{tr('payments.party')}</th>
                <th style={{ textAlign: 'left' }}>{tr('common.type')}</th>
                <th style={{ textAlign: 'left' }}>{tr('payments.billNo')}</th>
                <th style={{ textAlign: 'left' }}>{tr('payments.mode')}</th>
                <th style={{ textAlign: 'left' }}>{tr('payments.ref')}</th>
                <th style={{ textAlign: 'right' }}>{tr('payments.paid')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(payments || []).length === 0 ? (
                <tr><td colSpan="8" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                  {tr('payments.noPayments')}
                </td></tr>
              ) : (payments || []).map(p => (
                <tr key={p.id}>
                  <td style={{ textAlign: 'left', fontWeight: 600, fontFamily: 'monospace' }}>{p.payment_no}</td>
                  <td style={{ textAlign: 'left' }}>{p.party_name}</td>
                  <td style={{ textAlign: 'left', fontSize: 12, color: p.payment_type ? 'var(--text)' : 'var(--text-muted)' }}>
                    {p.payment_type ? tr(payTypeLabelKey(p.payment_type)) : '—'}
                  </td>
                  <td style={{ textAlign: 'left' }}>{p.bill_no || '—'}</td>
                  <td style={{ textAlign: 'left' }}>{p.mode}</td>
                  <td style={{ textAlign: 'left', fontSize: 11.5, color: 'var(--text-muted)' }}>{p.reference_no || '—'}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>
                    ₹ {fmtNum(p.paid_amount)}
                  </td>
                  <td>
                    <button className="btn btn-sm" style={{ color: 'var(--danger)' }}
                            onClick={() => setConfirm(p)}>🗑</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirm}
        title={tr('payments.deleteTitle')}
        message={confirm ? tr('payments.deleteMsg', { no: confirm.payment_no }) : ''}
        onConfirm={handleDelete}
        onCancel={() => setConfirm(null)}
        confirmLabel={tr('common.delete')}
      />
    </div>
  );
}
